/**
 * Created by christiancueni on 29/07/16.
 * Copyright (c) 2016 ITerativ GmbH. All rights reserved.
 */
import { Observable ,  Subscription } from 'rxjs';

import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState, hasPremium } from '../reducers';
import { getAppPart } from '../reducers';

import { LogoutAction } from '../actions/authentication';
import { MediaQueryService } from '../services/media-query';
import { OfflineService } from '../app/offline.service';

const NAV_MIN_WIDTH = 992;
const NAVIGATION_ELEMENTS = [
    {
        key: 'klassenbuch',
        link: '/klassenbuch',
        linkText: 'Klassenbuch',
        cssClass: 'qa-events-link',
        qaSelector: 'events-link',
        isPremium: false
    },
    {
        key: 'documents',
        link: '/klassenbuch/documents',
        linkText: 'Dateien',
        cssClass: 'qa-documents-link',
        qaSelector: 'documents-link',
        isPremium: true
    },
    {
        key: 'notifications',
        link: '/klassenbuch/notifications',
        linkText: 'Meldungen',
        cssClass: 'qa-notifications-link',
        qaSelector: 'notifications-link',
        isPremium: false
    },
    {
        key: 'profile',
        link: '/klassenbuch/profile',
        linkText: 'Profil',
        cssClass: 'qa-user-link',
        qaSelector: 'user-link',
        isPremium: false
    },
    {
        key: 'absences',
        link: '/absences/users',
        linkText: 'Absenzen',
        cssClass: '',
        qaSelector: 'absences-link',
        isPremium: true
    }
];

const ADMIN_NAVIGATION_ELEMENTS = [
    {
        key: 'school',
        link: '/admin/school',
        linkText: 'Einstellungen',
        cssClass: '',
        qaSelector: 'school-link',
        isPremium: false
    },
    {
        key: 'groups',
        link: '/admin/groups',
        linkText: 'Gruppen',
        cssClass: '',
        qaSelector: 'groups-link',
        isPremium: true
    },
    {
        key: 'person',
        link: '/admin/users',
        linkText: 'Personen',
        cssClass: '',
        qaSelector: 'users-link',
        isPremium: false
    },
    {
        key: 'labels',
        link: '/admin/labels',
        linkText: 'Labels',
        cssClass: '',
        qaSelector: 'labels-link',
        isPremium: false
    },
    {
        key: 'roles',
        link: '/admin/roles',
        linkText: 'Berechtigungen',
        cssClass: 'qa-roles-link',
        qaSelector: 'roles-link',
        isPremium: false
    }
];

@Component({
    selector: 'hc-navigation',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <nav class="main-navigation-wrapper" [ngClass]="{'is-shown': showNavigation}">
            <ul class="unstyled-list main-navigation">
                <li class="main-navigation__item main-navigation__item--subtle user-panel main-navigation__item--user-panel">
                    <p class="user__name no-margin fade-out">{{username}}<span
                        class="fade-out__gradient fade-out__gradient--blue"></span></p>
                    <p class="no-margin" data-cy="school-name">{{schoolName}}</p>
                </li>
                <li>
                    <ul class="unstyled-list user-navigation">
                        <ng-container *ngFor="let navElement of navElements">
                            <li *ngIf="showNaveElement(navElement.isPremium, (this.hasPremium$ | async))"
                                [ngClass]="{'main-navigation__item--active': (this.activeAppPart$ | async) === navElement.key,
                                            'main-navigation__item user-panel': true}">
                                <p class="no-margin">
                                    <a [ngClass]="navElement.cssClass"
                                       [attr.data-cy]="navElement.qaSelector"
                                       [routerLink]="[navElement.link]"
                                       >{{navElement.linkText | translate}}</a>
                            </li>
                        </ng-container>
                        <li class="main-navigation__item user-panel">
                            <p class="no-margin">
                                <a data-cy="logout"
                                   role="link"
                                   (click)="logout()">{{'Abmelden' | translate}}</a>
                            </p>
                        </li>
                    </ul>
                </li>
                <li *ngIf="showAdmin">
                    <ul class="unstyled-list admin-navigation">
                        <li class="main-navigation__item main-navigation__item--subtle main-navigation__item--title admin-panel">
                            <p class="no-margin">{{'Schuladministration' | translate}}</p>
                        </li>
                        <li *ngFor="let adminNavElement of adminNavElements"
                            [ngClass]="{'main-navigation__item--active': (this.activeAppPart$ | async) === adminNavElement.key,
                                        'main-navigation__item admin-panel': true}">
                            <p class="no-margin">
                                <a [ngClass]="adminNavElement.cssClass"
                                   [attr.data-cy]="adminNavElement.qaSelector"
                                   [routerLink]="[adminNavElement.link]"
                                   >{{adminNavElement.linkText | translate}}</a>
                        </li>
                    </ul>
                </li>
            </ul>
        </nav>
    `
})
export class NavigationComponent {

    @Input() showNavigation: boolean;
    @Input() username: string;
    @Input() schoolName: string;
    @Input() showAdmin: boolean;
    @Input() groupNames: string[];

    private mediaSubscription: Subscription;
    public activeAppPart$: Observable<string>;
    private navigationQuery: boolean;
    public hasPremium$: Observable<boolean>;
    public navElements = NAVIGATION_ELEMENTS;
    public adminNavElements = ADMIN_NAVIGATION_ELEMENTS;

    constructor(private router: Router,
                private store: Store<AppState>,
                private mediaQueryService: MediaQueryService) {

        this.mediaSubscription = this.mediaQueryService.minWidth(NAV_MIN_WIDTH)
            .subscribe((navigationQuery) => this.navigationQuery = navigationQuery);

        this.activeAppPart$ = this.store.select(getAppPart);
        this.hasPremium$ = this.store.select(hasPremium);
    }

    public logout() {
        this.store.dispatch(new LogoutAction(true));
    }

    public showNaveElement (requiresPremium, hasPremium: boolean): boolean {
        return !requiresPremium || hasPremium;
    }

    ngOnDestroy() {
        this.mediaSubscription.unsubscribe();
    }

}
